import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import { Tooltip } from 'components/Widget/Tooltip';
import { FrameStatus } from 'dragonmouse-shared/interfaces';
import { useImageLoading } from 'hooks/useImageLoading';
import { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PlacesType } from 'react-tooltip';
import { selectActiveScene, selectFrame } from 'redux/db/reducers';
import { canEdit, findNextFramePosition, getFrameBounds, SearchDirection } from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setCenteredFrameId,
	setCenterFramesAsChatGeneratesThem,
	setSelected,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { frameImageUrl, frameImageUrlById } from 'utils';
import SmallIconButton, { SmallIconButtonState } from '../Button/SmallIconButton';
import * as S from './styles';

export enum ThumbnailState {
	REGULAR,
	DISABLED,
}

export enum ThumbnailSize {
	RIGHT_PANEL = 104,
	CHAT = 84,
	MODAL = 80,
}

interface ThumbnailProps {
	frameId: string;
	tooltip?: string;
	tooltipPlacement?: PlacesType;
	onDelete?: () => void;
	size: ThumbnailSize;

	// the default on-click behavior is to center the frame on the canvas
	// if selectOnClick is true, the frame is also selected on click
	selectOnClick?: boolean;

	// if onClick is set, it will override the default on-click behavior (it will be called instead of the default)
	onClick?: () => void;
}

// HACK
// HACK
// it is expected that the thumbnail's parent element will have a data-frame-id attribute
// this is currently depended upon by Conversation (but there is no way to really document
// or enforce it in code). So for now hopefully this comment will suffice.
// HACK
// HACK
const Thumbnail = ({
	frameId,
	onDelete,
	tooltip,
	tooltipPlacement,
	size,
	selectOnClick,
	onClick,
}: ThumbnailProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [tooltipId] = useState(String(Math.random()));
	const [isHovered, setIsHovered] = useState(false);
	const ref = useRef(null);

	const { frame, scene, isMobile } = useSelector(
		(r: RootReducer) => ({
			// make sure this includes deleted frames because the chat can show thumbnails of frames that have been deleted from the canvas
			frame: selectFrame(r.db, frameId),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);

	// When the frame isn't in Redux (e.g. DS chat opened without the scene), fall back to
	// frameImageUrlById which hits /frame/:id/image. The backend resolves veHash etc. itself.
	// On local dev this can briefly show a loading/broken image while waiting for stream to
	// deliver a newly-created frame to Redux — this is transient and self-corrects.
	const imageSrc = frame ? frameImageUrl(frame) : frameImageUrlById(frameId);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');
	const tooltipString = tooltip ?? frame?.title;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onClickThumbnail = async () => {
		if (onClick) {
			onClick();
			return;
		}
		if (!frame) return;
		if (isMobile) {
			dispatch(setCenteredFrameId(frame.id));
		} else {
			if (frame && frame.deletedAt) {
				// Check if the scene is view-only before attempting to restore deleted frames
				if (!canEdit(scene)) {
					displayViewOnlyModal();
					return;
				}

				// try to put the frame back where it was
				// if that space is occupied, move it down until we find an empty space
				//
				// should we search downward from the current frame center instead of the old position?
				// that may make sense. this is not a use case we've seen enough to get a great intuition
				// for what the right answer is here.
				const bounds = findNextFramePosition(getFrameBounds(frame), SearchDirection.DOWN);
				await forge.dbrUpdateFrame({
					update: {
						id: frame.id,
						deletedAt: null,
						x: bounds.x,
						y: bounds.y,
					},
				});
				dispatch(setCenterFramesAsChatGeneratesThem(false));
				dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: bounds,
						animated: true,
						frameId: frame.id,
					})
				);
				if (selectOnClick) {
					dispatch(setSelected([new Pick(frame.id)]));
				}
			} else {
				dispatch(setCenterFramesAsChatGeneratesThem(false));
				frame &&
					dispatch(
						centerCanvasAroundSceneBoundingBox({
							boundingBox: getFrameBounds(frame),
							animated: true,
							frameId: frame.id,
						})
					);
				if (selectOnClick) {
					dispatch(setSelected([new Pick(frame.id)]));
				}
			}
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderLoadingIndicator = () => {
		return (
			<S.Error
				onClick={() => {
					onClickThumbnail();
				}}
				size={size}
			>
				<Loader width={32} height={32} />
			</S.Error>
		);
	};

	const renderErrorIndicator = () => {
		return (
			<S.Loader
				onClick={() => {
					onClickThumbnail();
				}}
				size={size}
			>
				<Icon type={IconType.ERROR} width={32} height={32} color={theme.gray[700]} />
			</S.Loader>
		);
	};

	const renderFrameImage = () => {
		return (
			<>
				<S.Image
					// this key is important to manage image loading correctly
					key={imageSrc + 'thumbnail'}
					ref={handleImageRef}
					src={imageSrc ?? undefined}
					onClick={() => {
						onClickThumbnail();
					}}
					size={size}
				/>
				{isHovered && onDelete && (
					<S.Icon>
						<SmallIconButton
							state={SmallIconButtonState.REGULAR}
							iconType={IconType.CLOSE}
							onClick={() => {
								onDelete?.();
							}}
						/>
					</S.Icon>
				)}
			</>
		);
	};

	const renderContents = () => {
		if (!frame) {
			return renderFrameImage();
		} else if (isImageLoading || frame.metadata?.status === FrameStatus.PROCESSING) {
			return renderLoadingIndicator();
		} else if (frame.metadata?.status === FrameStatus.ERROR) {
			return renderErrorIndicator();
		} else {
			return renderFrameImage();
		}
	};

	const placement: PlacesType = tooltipPlacement ?? 'bottom';
	return (
		<>
			<a data-tooltip-id={tooltipId} data-tooltip-content={tooltipString}>
				<S.Base
					ref={ref}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					size={size}
					data-frame-id={frameId}
				>
					{renderContents()}
				</S.Base>
			</a>
			<Tooltip id={tooltipId} placement={placement} />
		</>
	);
};

export default Thumbnail;
